import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { 
  Key, 
  Plus, 
  Edit, 
  Trash2, 
  Mail, 
  CreditCard, 
  Cloud,
  AlertCircle,
  Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAPIKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [formData, setFormData] = useState({
    provider: '',
    key_name: '',
    key_value: ''
  });

  const serviceProviders = [
    {
      value: 'mailgun',
      label: 'Mailgun',
      icon: Mail,
      description: 'Email delivery service'
    },
    {
      value: 'nowpayments',
      label: 'NOWPayments',
      icon: CreditCard,
      description: 'Cryptocurrency payment processor'
    },
    {
      value: 'cloudinary',
      label: 'Cloudinary',
      icon: Cloud,
      description: 'Image and video management'
    }
  ];

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/admin/api-keys`);
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.provider || !formData.key_name || !formData.key_value) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      if (editingKey) {
        await axios.put(`${API}/admin/api-keys/${editingKey.id}`, formData);
        toast.success('API key updated successfully');
      } else {
        await axios.post(`${API}/admin/api-keys`, formData);
        toast.success('API key added successfully');
      }
      
      setShowDialog(false);
      setEditingKey(null);
      setFormData({ provider: '', key_name: '', key_value: '' });
      fetchAPIKeys();
    } catch (error) {
      console.error('Failed to save API key:', error);
      const message = error.response?.data?.detail || 'Failed to save API key';
      toast.error(message);
    }
  };

  const handleEdit = (key) => {
    setEditingKey(key);
    setFormData({
      provider: key.provider,
      key_name: key.key_name,
      key_value: '' // Don't pre-fill the actual key value for security
    });
    setShowDialog(true);
  };

  const handleDelete = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/api-keys/${keyId}`);
      toast.success('API key deleted successfully');
      fetchAPIKeys();
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const getProviderInfo = (provider) => {
    return serviceProviders.find(p => p.value === provider) || {
      label: provider,
      icon: Key,
      description: 'External service'
    };
  };

  const resetForm = () => {
    setFormData({ provider: '', key_name: '', key_value: '' });
    setEditingKey(null);
  };

  if (loading) {
    return (
      <AdminLayout title="API Keys Management">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="API Keys Management">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Service Integrations</h2>
          <p className="text-sm text-gray-600">
            Configure API keys for external services and integrations
          </p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add API Key
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingKey ? 'Update API Key' : 'Add New API Key'}
              </DialogTitle>
              <DialogDescription>
                Configure API keys for external service integrations
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="provider">Service Provider</Label>
                <Select 
                  value={formData.provider} 
                  onValueChange={(value) => setFormData({...formData, provider: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceProviders.map((provider) => {
                      const Icon = provider.icon;
                      return (
                        <SelectItem key={provider.value} value={provider.value}>
                          <div className="flex items-center">
                            <Icon className="h-4 w-4 mr-2" />
                            <div>
                              <div className="font-medium">{provider.label}</div>
                              <div className="text-xs text-gray-500">{provider.description}</div>
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="key_name">Key Name</Label>
                <Input
                  id="key_name"
                  placeholder="e.g., API_KEY, SECRET_KEY, DOMAIN"
                  value={formData.key_name}
                  onChange={(e) => setFormData({...formData, key_name: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="key_value">Key Value</Label>
                <Input
                  id="key_value"
                  type="password"
                  placeholder="Enter the API key or secret"
                  value={formData.key_value}
                  onChange={(e) => setFormData({...formData, key_value: e.target.value})}
                  required
                />
                {editingKey && (
                  <p className="text-xs text-gray-500">
                    Leave empty to keep the existing key value
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  {editingKey ? 'Update Key' : 'Add Key'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Service Provider Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {serviceProviders.map((provider) => {
          const Icon = provider.icon;
          const configuredKeys = apiKeys.filter(key => key.provider === provider.value);
          
          return (
            <Card key={provider.value} className="border-2 hover:border-yellow-300 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                      <Icon className="h-5 w-5 text-black" />
                    </div>
                    <div className="ml-3">
                      <CardTitle className="text-lg">{provider.label}</CardTitle>
                      <CardDescription className="text-sm">
                        {provider.description}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant={configuredKeys.length > 0 ? "secondary" : "outline"}>
                    {configuredKeys.length} key{configuredKeys.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {configuredKeys.length > 0 ? (
                  <div className="space-y-2">
                    {configuredKeys.map((key) => (
                      <div key={key.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center">
                          <Check className="h-4 w-4 text-green-500 mr-2" />
                          <span className="text-sm font-medium">{key.key_name}</span>
                        </div>
                        <div className="flex space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(key)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(key.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No keys configured</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* All API Keys Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Configured Keys</CardTitle>
          <CardDescription>
            Complete list of all API keys and their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {apiKeys.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Key Name</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((key) => {
                    const providerInfo = getProviderInfo(key.provider);
                    const Icon = providerInfo.icon;
                    
                    return (
                      <tr key={key.id}>
                        <td>
                          <div className="flex items-center">
                            <Icon className="h-4 w-4 mr-2 text-gray-600" />
                            <span className="font-medium">{providerInfo.label}</span>
                          </div>
                        </td>
                        <td>
                          <span className="font-mono text-sm">{key.key_name}</span>
                        </td>
                        <td>
                          <Badge 
                            variant={key.is_active ? "secondary" : "outline"}
                            className={key.is_active ? "bg-green-100 text-green-800" : ""}
                          >
                            {key.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <span className="text-sm text-gray-600">
                            {new Date(key.created_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(key)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(key.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No API Keys Configured</h3>
              <p className="text-gray-600 mb-4">
                Start by adding API keys for your external service integrations
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminAPIKeys;